import React from 'react';
import styles from './css/index.module.css';
import { Select as FluentSelect, Textarea } from '@fluentui/react-components';
import Select from 'react-select';
import { PatchProjectData } from '../../redux/projectDataSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import {
    ENGAGEMENT_OBJECTIVE_PLACEHOLDER,
    newProjectDetailsFormValues,
    PROJECT_SCOPE_SELECT_STYLES,
    resolveWorkspaceCustomerStageOptions,
    resolveWorkspaceCustomerTypeOptions,
    resolveWorkspaceScopeAreaOptions,
} from '../../utils/projectMetadataOptions';

const AddProjectDetails = ({ setSelectedView, createdProjectData, setCreatedProjectData }) => {
    const dispatch = useDispatch();
    const { projectDetailButtonLoading } = useSelector(state => state.projectData);
    const memberships = useSelector(state => state?.user?.memberships || []);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId) || localStorage.getItem('active_workspace_id');

    const [formData, setFormData] = React.useState(newProjectDetailsFormValues());

    const activeTenant = React.useMemo(() => {
        const m = memberships.find(mem => mem?.workspace?.id === activeWorkspaceId);
        return m?.workspace || null;
    }, [memberships, activeWorkspaceId]);

    const customerTypeOptions = React.useMemo(
        () => resolveWorkspaceCustomerTypeOptions(activeTenant, { includeValue: formData?.customer_type }),
        [activeTenant, formData?.customer_type],
    );

    const customerStageOptions = React.useMemo(
        () => resolveWorkspaceCustomerStageOptions(activeTenant, { includeValue: formData?.customer_stage }),
        [activeTenant, formData?.customer_stage],
    );

    const scopeAreaOptions = React.useMemo(
        () => resolveWorkspaceScopeAreaOptions(activeTenant, { includeValues: formData?.scope_areas || [] }),
        [activeTenant, formData?.scope_areas],
    );

    const scopeAreaSelectOptions = React.useMemo(
        () => scopeAreaOptions.map(option => ({ value: option.value, label: option.text })),
        [scopeAreaOptions],
    );

    const selectedScopeAreas = React.useMemo(
        () => scopeAreaSelectOptions.filter(option => (formData?.scope_areas || []).includes(option.value)),
        [scopeAreaSelectOptions, formData?.scope_areas],
    );

    const handleChange = e => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleScopeAreasChange = selectedOptions => {
        setFormData(prev => ({
            ...prev,
            scope_areas: (selectedOptions || []).map(option => option.value),
        }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        if (!createdProjectData?.id) return;

        const patchData = {
            customer_type: formData.customer_type,
            customer_stage: formData.customer_stage,
            scope_areas: formData.scope_areas,
            engagement_objective: formData.engagement_objective?.trim() || '',
        };

        await dispatch(
            PatchProjectData({
                id: createdProjectData.id,
                data: patchData,
            }),
        );

        setCreatedProjectData({
            ...createdProjectData,
            ...patchData,
        });
        setSelectedView('uploadNda');
    };

    const disableFormBtn =
        !formData?.customer_type ||
        !formData?.customer_stage ||
        projectDetailButtonLoading ||
        !createdProjectData?.id;

    return (
        <div className={styles.componentCard} style={{ width: '560px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
            <h5 className={styles.componentTitle}>Add Project Details</h5>
            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-sm-12 col-md-6 mb-4">
                        <p className={styles.fieldLabel}>
                            Company Stage <span className={styles.required}>*</span>
                        </p>
                        <FluentSelect className={styles.selectField} required value={formData?.customer_stage} onChange={handleChange} name="customer_stage">
                            <option value="">Select</option>
                            {customerStageOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.text}
                                </option>
                            ))}
                        </FluentSelect>
                    </div>
                    <div className="col-sm-12 col-md-6 mb-4">
                        <p className={styles.fieldLabel}>
                            Customer Type <span className={styles.required}>*</span>
                        </p>
                        <FluentSelect className={styles.selectField} required value={formData?.customer_type} onChange={handleChange} name="customer_type">
                            <option value="">Select</option>
                            {customerTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.text}
                                </option>
                            ))}
                        </FluentSelect>
                    </div>
                </div>
                <div className="mb-4">
                    <p className={styles.fieldLabel}>Scope Areas</p>
                    <Select
                        isMulti
                        closeMenuOnSelect={false}
                        options={scopeAreaSelectOptions}
                        value={selectedScopeAreas}
                        onChange={handleScopeAreasChange}
                        placeholder="Select scope areas"
                        styles={PROJECT_SCOPE_SELECT_STYLES}
                        classNamePrefix="projectScopeSelect"
                    />
                </div>
                <div className="mb-5">
                    <p className={styles.fieldLabel}>Engagement Objective</p>
                    <Textarea
                        className={styles.textareaField}
                        value={formData?.engagement_objective}
                        onChange={handleChange}
                        name="engagement_objective"
                        rows={8}
                        placeholder={ENGAGEMENT_OBJECTIVE_PLACEHOLDER}
                    />
                </div>

                <button disabled={disableFormBtn} type="submit" className={styles.submitButton}>
                    Next
                    {projectDetailButtonLoading && <AiOutlineLoading3Quarters className="ms-2 rotateLoader" />}
                </button>
            </form>
        </div>
    );
};

export default AddProjectDetails;
